import { TodoItem, CreateTodoRequest, UpdateTodoRequest, TodoList, Comment, PublicTodoList } from '../types/todo';
import { apiCall } from './authApi';

// Helper function to handle fetch responses
export const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    let error;
    
    if (contentType && contentType.includes('application/json')) {
      try {
        error = await response.json();
      } catch {
        error = { error: `Request failed with status ${response.status}` };
      }
    } else {
      error = { error: `Request failed with status ${response.status}` };
    }
    
    throw new Error(error.error || `Request failed with status ${response.status}`);
  }
  
  // Handle empty responses (like DELETE operations)
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return null;
};

export const todoApi = {
  // Todo Lists
  getAllLists: async (userEmail: string): Promise<TodoList[]> => {
    try {
      const response = await apiCall(`/todolists?userEmail=${userEmail}`);
      return handleResponse(response);
    } catch (error) {
      if (error instanceof Error && error.message === 'Authentication required') {
        throw error;
      }
      console.error('Failed to get lists:', error);
      return [];
    }
  },

  getList: async (id: number): Promise<TodoList> => {
    const response = await apiCall(`/todolists/${id}`);
    return handleResponse(response);
  },

  getListWithUserEmail: async (id: number, userEmail: string): Promise<TodoList> => {
    const response = await apiCall(`/todolists/${id}?userEmail=${encodeURIComponent(userEmail)}`);
    return handleResponse(response);
  },

  createList: async (data: { 
    name: string; 
    description?: string; 
    color?: string;
    isShared?: boolean;
    sharedWith?: string[];
  }, userEmail: string): Promise<TodoList> => {
    const payload = {
      name: data.name,
      description: data.description || "",
      createdBy: userEmail,
      isShared: data.isShared || false,
      sharedWith: data.sharedWith || [],
      color: data.color || "#3B82F6"
    };
    console.log('[todoApi.createList] Creating list with payload:', payload);
    const response = await apiCall('/TodoLists', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    const result = await handleResponse(response);
    console.log('[todoApi.createList] List created, response:', result);
    return result;
  },

  updateList: async (id: number, data: { name: string; description?: string; color?: string; isPublic?: boolean }, userEmail: string): Promise<TodoList> => {
    const response = await apiCall(`/todolists/${id}?userEmail=${userEmail}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return handleResponse(response);
  },

  deleteList: async (id: number, userEmail: string): Promise<void> => {
    const response = await apiCall(`/todolists/${id}?userEmail=${userEmail}`, {
      method: 'DELETE'
    });
    await handleResponse(response);
  },

  // Todo Items - Get all todos from user's lists
  getAllTodos: async (userEmail: string): Promise<TodoItem[]> => {
    try {
      // Get all todo lists for the user
      const lists = await todoApi.getAllLists(userEmail);
      
      if (Array.isArray(lists)) {
        // Extract all todos from all lists
        const allTodos: TodoItem[] = [];
        lists.forEach((list: TodoList) => {
          if (list.items && Array.isArray(list.items)) {
            allTodos.push(...list.items);
          }
        });
        return allTodos;
      }
    } catch (error) {
      console.error('getAllTodos - error:', error);
    }

    return [];
  },

  getTodo: async (id: number, userEmail: string): Promise<TodoItem> => {
    const response = await apiCall(`/TodoItems/${id}?userEmail=${userEmail}`);
    return handleResponse(response);
  },

  createTodo: async (data: CreateTodoRequest, userEmail: string): Promise<TodoItem> => {
    // listId should be provided by the caller (useCreateTodo hook ensures this)
    if (!data.listId) {
      throw new Error('Cannot create todo without a listId. This should be handled by useCreateTodo hook.');
    }
    
    // Prepare the payload to match backend expectations
    const payload = {
      title: data.title,
      description: data.description || "",
      createdBy: data.createdBy || userEmail,
      assignedTo: data.assignedTo,
      priority: data.priority || "Medium",
      dueDate: data.dueDate,
      reminderDateTime: data.reminderDateTime,
      tags: Array.isArray(data.tags) ? data.tags.join(',') : data.tags, // Convert array to comma-separated string
      status: data.status,
      listId: data.listId
    };
    
    console.log('[todoApi.createTodo] Creating todo with payload:', payload);
    const response = await apiCall('/TodoItems', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    const result = await handleResponse(response);
    console.log('[todoApi.createTodo] Todo created, response:', result);
    return result;
  },

  updateTodo: async (id: number, data: UpdateTodoRequest, userEmail: string): Promise<TodoItem> => {
    // Convert tags array to comma-separated string for backend
    const payload = {
      ...data,
      tags: Array.isArray(data.tags) ? data.tags.join(',') : data.tags,
      // Set flags to indicate which fields were explicitly provided
      isDueDateSet: data.dueDate !== undefined,
      isReminderDateTimeSet: data.reminderDateTime !== undefined,
      isStatusSet: data.status !== undefined,
      isEisenhowerClassificationSet: data.eisenhowerClassification !== undefined,
      isAssignedToSet: data.assignedTo !== undefined
    };
    
    console.log('updateTodo payload:', payload); // Debug logging
    
    const response = await apiCall(`/TodoItems/${id}?userEmail=${userEmail}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    return handleResponse(response);
  },

  deleteTodo: async (id: number): Promise<void> => {
    const response = await apiCall(`/TodoItems/${id}`, {
      method: 'DELETE'
    });
    await handleResponse(response);
  },

  toggleTodo: async (id: number, userEmail: string): Promise<TodoItem> => {
    // Use dedicated PATCH endpoint for atomic toggle operation
    // This eliminates the race condition from the previous GET + PUT approach
    const response = await apiCall(`/TodoItems/${id}/toggle?userEmail=${userEmail}`, {
      method: 'PATCH'
    });
    return handleResponse(response);
  },

  // Comments
  addComment: async (itemId: number, commentText: string, userEmail: string): Promise<Comment> => {
    const response = await apiCall(`/TodoItems/${itemId}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content: commentText,
        authorEmail: userEmail
      })
    });
    return handleResponse(response);
  },

  // List Sharing
  updateListSharing: async (listId: number, isShared: boolean, sharedWith: string[], userEmail: string): Promise<TodoList> => {
    const response = await apiCall(`/TodoLists/${listId}?userEmail=${userEmail}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isShared,
        sharedWith
      })
    });
    return handleResponse(response);
  },

  shareListWithUser: async (listId: number, userEmail: string, currentSharedWith: string[], ownerEmail: string): Promise<TodoList> => {
    const updatedSharedWith = [...currentSharedWith, userEmail];
    return todoApi.updateListSharing(listId, true, updatedSharedWith, ownerEmail);
  },

  unshareListFromUser: async (listId: number, userEmail: string, currentSharedWith: string[], ownerEmail: string): Promise<TodoList> => {
    const updatedSharedWith = currentSharedWith.filter(email => email !== userEmail);
    return todoApi.updateListSharing(listId, updatedSharedWith.length > 0, updatedSharedWith, ownerEmail);
  },

  makeListPrivate: async (listId: number, ownerEmail: string): Promise<TodoList> => {
    return todoApi.updateListSharing(listId, false, [], ownerEmail);
  },

  // Public API (no authentication required)
  getPublicList: async (token: string): Promise<PublicTodoList> => {
    const response = await fetch(`/api/todolists/public/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return handleResponse(response);
  },
};
